import { z } from 'zod';
import { clientTypeSchema } from '../enums';
import { passwordPolicyReasonSchema } from '../password-policy';
import { MEMBER_GRANTS } from '../tasks';
import {
  boundedList,
  deviceNameSchema,
  emailOutSchema,
  emailSchema,
  isoDateTimeSchema,
  opaqueTokenSchema,
  otpCodeSchema,
  passwordSchema,
  uuidSchema,
} from './common';
import { memberDtoSchema, workspaceDtoSchema } from './org';

/**
 * Auth and session contracts (PLAN §4 "Phase 0 — Auth", "Phase 0 — Sessions", D-008, D-073).
 * Web clients get the refresh token as the httpOnly `hub_rt` cookie and the trusted-device
 * credential as `hub_td`; native clients (`clientType` mobile/desktop) get both in the body.
 */

// ---------------------------------------------------------------- cookies and headers

/** Refresh-token cookie (httpOnly, SameSite=Strict, Path=/api/v1/auth). */
export const REFRESH_COOKIE = 'hub_rt';
/** Trusted-device cookie, 30 days (D-008). */
export const TRUSTED_DEVICE_COOKIE = 'hub_td';
/** Both auth cookies are scoped to this path, so only the auth routes ever see them. */
export const AUTH_COOKIE_PATH = '/api/v1/auth';
/** CSRF header required (value `1`) on the cookie routes /auth/refresh and /auth/logout. */
export const CSRF_HEADER = 'x-hub-csrf';

/** Access tokens live 15 minutes (D-008). */
export const ACCESS_TOKEN_TTL_SECONDS = 15 * 60;
/** Sessions: idle expiry 30 days, absolute expiry 90 days (D-008). */
export const SESSION_IDLE_TTL_SECONDS = 30 * 24 * 3600;
export const SESSION_ABSOLUTE_TTL_SECONDS = 90 * 24 * 3600;
/** A rotated refresh token reused within this window answers `refresh_race` (D-008). */
export const REFRESH_RACE_WINDOW_SECONDS = 30;
/** Trusted-device credentials are honoured for 30 days (D-008). */
export const TRUSTED_DEVICE_TTL_SECONDS = 30 * 24 * 3600;
/** OTP codes: valid 10 minutes, 5 attempts, 30 s resend cooldown, 5 sends per hour (D-008). */
export const OTP_CODE_TTL_SECONDS = 10 * 60;
export const OTP_CHALLENGE_TTL_SECONDS = 30 * 60;
export const OTP_MAX_ATTEMPTS = 5;
export const OTP_RESEND_COOLDOWN_SECONDS = 30;
export const OTP_MAX_SENDS_PER_HOUR = 5;
/** Password-reset codes: 10 minutes, 5 attempts. */
export const RESET_CODE_TTL_SECONDS = 10 * 60;

// ---------------------------------------------------------------- DTOs

/** Global identity (D-005); only ever returned for the caller. */
export const userDtoSchema = z.object({
  id: uuidSchema,
  email: emailOutSchema,
  fullName: z.string(),
  emailVerifiedAt: isoDateTimeSchema.nullable(),
  passwordChangedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type UserDto = z.infer<typeof userDtoSchema>;

/**
 * What every chat boot and Frill token carries, so one shared inbox and one shared Frill project
 * can tell the Comsky products apart: announcements are segmented on it, and without it a
 * colleague here arrives in the agent's queue looking like a customer of something else.
 */
export const SUPPORT_PRODUCT = 'orbit';

/**
 * The two in-product support widgets: Comsky Chat and Frill (D-1241).
 *
 * Both halves are proofs the API mints, because a proof the browser can compute is no proof at
 * all. Either is null when its secret is unset — the chat widget then opens as an anonymous
 * visitor and Frill renders signed-out. That degradation is deliberate and it is SILENT at both
 * vendors' ends, which is why the server logs it rather than leaving it to be discovered from an
 * inbox full of "Visitor 3f21c8".
 *
 * The public halves (`widgetKey`, `apiBase`, `widgetId`) ride here rather than in the bundle so
 * that pointing a deployment at another chat workspace or Frill project is a `.env` change and a
 * restart, not a web rebuild. None of them is a secret: the key scopes a widget session and
 * nothing more, and the bundle it names is one byte-identical public file.
 */
export const supportWidgetsDtoSchema = z.object({
  /** Null when this deployment configures no chat widget. */
  chat: z
    .object({
      /** The workspace's public `wgt_…` key, from the host page's script tag. */
      widgetKey: z.string(),
      /** The origin that serves both the bundle and the endpoints the widget then calls. */
      apiBase: z.string(),
      /** Null when the identity secret is unset: the widget boots anonymous. */
      identity: z.object({ userId: z.string(), userHash: z.string() }).nullable(),
    })
    .nullable(),
  /** Null when this deployment configures no Frill widget. */
  frill: z
    .object({
      widgetId: z.string(),
      /** Null when the SSO secret is unset: announcements render signed-out. */
      ssoToken: z.string().nullable(),
    })
    .nullable(),
});
export type SupportWidgetsDto = z.infer<typeof supportWidgetsDtoSchema>;

export const meDtoSchema = z.object({
  user: userDtoSchema,
  /** The caller's membership in the token's workspace. */
  member: memberDtoSchema,
  workspace: workspaceDtoSchema,
  /** Matrix actions the caller may perform (`permissionsFor(role, settings)`). */
  permissions: z.array(z.string()),
  /**
   * The caller's own `member_grants` (`cockpit`, `hr_compensation`, `payroll_export`,
   * `pay_approver`; wave 4, D-801): not ranks, so not in `permissions`. The web app uses them only
   * to decide which navigation entries to show; the API checks every grant itself. Absent from an
   * older server: read it as none.
   */
  grants: z.array(z.enum(MEMBER_GRANTS)).optional(),
  /**
   * The active departments the caller heads (wave 4, D-926): a department head who is a plain
   * member may use the team views, so the navigation needs to know. Only for showing entries; the
   * API decides every request itself. Absent from an older server: read it as none.
   */
  headedDepartmentIds: z.array(uuidSchema).optional(),
  /**
   * The in-product support widgets and this person's credentials for them (D-1241). Absent from
   * an older server, and from a deployment that configures neither: read it as "no widgets", and
   * mount nothing rather than guessing at a key.
   */
  support: supportWidgetsDtoSchema.optional(),
  session: z.object({ id: uuidSchema, clientType: clientTypeSchema }),
});
export type MeDto = z.infer<typeof meDtoSchema>;

export const authSuccessSchema = z.object({
  otpRequired: z.literal(false),
  tokenType: z.literal('Bearer'),
  /** JWT HS256; claims sub (user id), wid (workspace id), sid (session id), iat, exp. */
  accessToken: z.string(),
  /** now + 15 min. */
  accessTokenExpiresAt: isoDateTimeSchema,
  sessionId: uuidSchema,
  /** Native clients only; web gets the hub_rt cookie. */
  refreshToken: z.string().nullable(),
  /** Native only: min(lastUsed + 30 d, createdAt + 90 d). */
  refreshTokenExpiresAt: isoDateTimeSchema.nullable(),
  /** Native only, when this response made the device trusted. */
  trustedDeviceToken: z.string().nullable(),
  me: meDtoSchema,
});
export type AuthSuccess = z.infer<typeof authSuccessSchema>;

export const otpChallengeSchema = z.object({
  otpRequired: z.literal(true),
  /** The challenge lives 30 minutes in total. */
  challengeId: uuidSchema,
  /** "ak•••@comhard.co.in". */
  emailHint: z.string(),
  /** now + 10 min. */
  codeExpiresAt: isoDateTimeSchema,
  /** now + 30 s. */
  resendAvailableAt: isoDateTimeSchema,
});
export type OtpChallenge = z.infer<typeof otpChallengeSchema>;

// ---------------------------------------------------------------- POST /auth/login

export const loginRequestSchema = z.strictObject({
  email: emailSchema,
  /** 1–1024 characters; the policy is not re-checked at login. */
  password: passwordSchema,
  clientType: clientTypeSchema.default('web'),
  /** Native clients only. */
  deviceName: deviceNameSchema.optional(),
  /** Native clients only; web uses the hub_td cookie. */
  trustedDeviceToken: z.string().min(1).max(512).optional(),
  /** Default: the only active membership, else the one with the latest last_active_at. */
  workspaceId: uuidSchema.optional(),
  /** Sign in and accept a pending invite (D-074); the OTP policy still applies (D-132). */
  inviteToken: opaqueTokenSchema.optional(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.discriminatedUnion('otpRequired', [
  authSuccessSchema,
  otpChallengeSchema,
]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

// ---------------------------------------------------------------- OTP

export const otpVerifyRequestSchema = z.strictObject({
  challengeId: uuidSchema,
  code: otpCodeSchema,
  /** Default true: web gets hub_td, native gets trustedDeviceToken. */
  trustDevice: z.boolean().default(true),
});
export type OtpVerifyRequest = z.infer<typeof otpVerifyRequestSchema>;

export const otpResendRequestSchema = z.strictObject({ challengeId: uuidSchema });
export type OtpResendRequest = z.infer<typeof otpResendRequestSchema>;

export const otpResendResponseSchema = z.object({
  codeExpiresAt: isoDateTimeSchema,
  resendAvailableAt: isoDateTimeSchema,
  emailHint: z.string(),
});
export type OtpResendResponse = z.infer<typeof otpResendResponseSchema>;

// ---------------------------------------------------------------- refresh and logout

/** Web: `{}` plus the hub_rt cookie, an allowed Origin and X-Hub-CSRF: 1. Native: the token. */
export const refreshRequestSchema = z.strictObject({
  refreshToken: opaqueTokenSchema.optional(),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

export const refreshResponseSchema = z.object({
  tokenType: z.literal('Bearer'),
  accessToken: z.string(),
  accessTokenExpiresAt: isoDateTimeSchema,
  sessionId: uuidSchema,
  /** Native only. */
  refreshToken: z.string().nullable(),
  refreshTokenExpiresAt: isoDateTimeSchema.nullable(),
});
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

export const logoutRequestSchema = z.strictObject({
  /** Native clients and the offline-logout retry send the refresh token to revoke (D-1109). */
  refreshToken: opaqueTokenSchema.optional(),
});
export type LogoutRequest = z.infer<typeof logoutRequestSchema>;

// ---------------------------------------------------------------- passwords

export const passwordResetRequestSchema = z.strictObject({ email: emailSchema });
export type PasswordResetRequest = z.infer<typeof passwordResetRequestSchema>;

/** Always 202, whether or not the account exists. */
export const passwordResetRequestResponseSchema = z.object({
  resetId: uuidSchema,
  codeExpiresAt: isoDateTimeSchema,
});
export type PasswordResetRequestResponse = z.infer<typeof passwordResetRequestResponseSchema>;

export const passwordResetConfirmSchema = z.strictObject({
  resetId: uuidSchema,
  code: otpCodeSchema,
  newPassword: passwordSchema,
});
export type PasswordResetConfirm = z.infer<typeof passwordResetConfirmSchema>;

/** POST /me/password. */
export const changePasswordRequestSchema = z.strictObject({
  currentPassword: passwordSchema,
  newPassword: passwordSchema,
  signOutOtherSessions: z.boolean().default(true),
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const changePasswordResponseSchema = z.object({
  /** Native only; web receives a fresh hub_td cookie and null here. */
  trustedDeviceToken: z.string().nullable(),
});

/** `details` of `422 password_policy_violation`. */
export const passwordPolicyViolationDetailsSchema = z.object({
  reasons: z.array(passwordPolicyReasonSchema),
});

// ---------------------------------------------------------------- sessions

export const sessionDtoSchema = z.object({
  id: uuidSchema,
  deviceLabel: z.string(),
  clientType: clientTypeSchema,
  /** Last seen IP. */
  ipAddress: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  /** Written at most once per 5 minutes. */
  lastSeenAt: isoDateTimeSchema,
  /** min(lastSeenAt + 30 d, createdAt + 90 d). */
  expiresAt: isoDateTimeSchema,
  current: z.boolean(),
});
export type SessionDto = z.infer<typeof sessionDtoSchema>;

/** GET /me/sessions — newest lastSeenAt first, bounded. */
export const sessionListResponseSchema = boundedList(sessionDtoSchema);

export const sessionIdParamsSchema = z.object({ sessionId: uuidSchema });

/** POST /me/sessions/revoke-others and POST /members/:userId/sessions/revoke-all. */
export const revokedCountResponseSchema = z.object({
  revokedCount: z.number().int().nonnegative(),
});
